import { handlePopoverRotation } from "@/utils/popover";
import React, { useRef } from "react";
import { FocusableOptions, Placement, useFocusable } from "react-aria";
import {
  Tooltip as AriaTooltip,
  TooltipProps as AriaTooltipProps,
  composeRenderProps,
  OverlayArrow,
  TooltipTrigger,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { ArrowView } from "../campaign/basic/ArrowView";

export interface TooltipProps extends Omit<AriaTooltipProps, "children"> {
  children: React.ReactNode;
  tooltipTriggerProps?: Partial<React.ComponentProps<typeof TooltipTrigger>>;
  tooltipText?: string | React.ReactNode;
  arrow?: boolean;
  arrowPosition?: Placement;
  arrowClassName?: string;
}

const styles = tv({
  base: "max-w-xs group colors-glass backdrop-blur-glass border border-theme-outline text-sm rounded-lg drop-shadow-lg will-change-transform px-3 py-2",
  variants: {
    isEntering: {
      true: "animate-in fade-in placement-bottom:slide-in-from-top-0.5 placement-top:slide-in-from-bottom-0.5 placement-left:slide-in-from-right-0.5 placement-right:slide-in-from-left-0.5 ease-out duration-200",
    },
    isExiting: {
      true: "animate-out fade-out placement-bottom:slide-out-to-top-0.5 placement-top:slide-out-to-bottom-0.5 placement-left:slide-out-to-right-0.5 placement-right:slide-out-to-left-0.5 ease-in duration-150",
    },
  },
});
export const Focusable = (
  props: React.JSX.IntrinsicElements["div"] & FocusableOptions<HTMLDivElement>,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const { focusableProps } = useFocusable(props, ref);
  return (
    <div
      {...focusableProps}
      ref={ref}
      className={twMerge(
        focusableProps.className,
        "outline-0 outline-offset-2 outline-blue-500 focus-visible:outline-2",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
};
export function Tooltip({
  children,
  tooltipTriggerProps,
  ...props
}: TooltipProps) {
  return (
    <TooltipTrigger delay={0} closeDelay={0} {...tooltipTriggerProps}>
      {children}

      {props.tooltipText && (
        <AriaTooltip
          {...props}
          offset={props.offset ?? 10}
          className={composeRenderProps(
            props.className,
            (className, renderProps) =>
              twMerge(styles({ ...renderProps }), className),
          )}
        >
          {props.arrow && (
            <OverlayArrow
              className={twMerge(
                props.arrowClassName,
                handlePopoverRotation(props.arrowPosition ?? "bottom"),
              )}
            >
              <ArrowView />
            </OverlayArrow>
          )}
          <div className="text-xs">{props.tooltipText}</div>
        </AriaTooltip>
      )}
    </TooltipTrigger>
  );
}
